"use client";

import { type JSX } from "react";
import { NextImage, Text } from "@sitecore-content-sdk/nextjs";
import { VideoComponentProps } from "components/video-component/video-component.props";
import { convertToEmbedUrl } from "lib/utils";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";
import VideoPlayer from "./VideoPlayer";
import styles from "./VideoComponent.module.scss";

interface VideoEditingViewProps {
  fields: NonNullable<VideoComponentProps["fields"]>;
  hiddenBlockStyles: string;
}

// In editing mode authors see all three fields stacked (no consent flow,
// no embedded iframe). Pure server render.
const VideoEditingView = ({
  fields,
  hiddenBlockStyles,
}: VideoEditingViewProps): JSX.Element => (
  <div
    className={`relative w-full overflow-hidden px-5 border ${hiddenBlockStyles}`}
  >
    <Text field={fields.Title} tag="h4" className="mb-5" />
    <Text field={fields.Url} tag="div" className="mb-5" />
    <NextImage field={fields.Thumbnail} className="mb-5" />
  </div>
);

interface VideoBodyProps {
  props: VideoComponentProps;
  hiddenBlockStyles: string;
}

const VideoBody = ({
  props,
  hiddenBlockStyles,
}: VideoBodyProps): JSX.Element | null => {
  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing ?? false;
  const isPreviewMode = page.mode.isPreview ?? false;

  if (!fields) return null;

  if (isPageEditing) {
    return (
      <VideoEditingView fields={fields} hiddenBlockStyles={hiddenBlockStyles} />
    );
  }

  const rawUrl = fields.Url?.value?.toString() ?? "";
  if (!rawUrl) return null;

  const autoplay = params?.EnableAutoplay === "1" ? "1" : "0";
  const controls = params?.ShowPlayerControl === "0" ? "0" : "1";
  const color =
    params?.PlayerColor?.toLowerCase() === "white" ? "white" : "red";
  const iframeUrl = `${rawUrl}?autoplay=${autoplay}&controls=${controls}&color=${color}`;
  const embedSrc = convertToEmbedUrl(iframeUrl);

  const thumbnailUrl = fields.Thumbnail?.value?.src ?? "";
  const thumbnailAltRaw = fields.Thumbnail?.value?.alt;
  const thumbnailAlt =
    typeof thumbnailAltRaw === "string" ? thumbnailAltRaw : "";
  const title = fields.Title?.value?.toString() ?? "";

  return (
    <VideoPlayer
      embedSrc={embedSrc}
      thumbnailUrl={thumbnailUrl}
      thumbnailAlt={thumbnailAlt}
      title={title}
      hiddenBlockStyles={hiddenBlockStyles}
      rawUrlForCookieMessage={rawUrl}
      isPreviewMode={isPreviewMode}
    />
  );
};

export const Default = (props: VideoComponentProps): JSX.Element => {
  console.log("---->[Video:Default] Component", props.fields);

  const { fields, params } = props;
  if (!fields) return <NoDataFallback componentName="Video Component" />;

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const wrapperClass = `max-w-[704px] mx-auto py-[var(--global-component-spacing)] px-4 ${styles.defaultVariant} ${params?.Styles ?? ""} ${hiddenBlockStyles}`;

  return (
    <div className={wrapperClass} data-role="video-component">
      <VideoBody props={props} hiddenBlockStyles={hiddenBlockStyles} />
    </div>
  );
};

export const FullWidth = (props: VideoComponentProps): JSX.Element => {
  console.log("---->[Video:FullWidth] Component", props.fields);

  const { fields, params } = props;
  if (!fields) return <NoDataFallback componentName="Video Component" />;

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const wrapperClass = `px-4 ${styles.fullWidthVariant} ${params?.Styles ?? ""} ${hiddenBlockStyles}`;

  return (
    <div className={wrapperClass} data-role="video-component">
      <VideoBody props={props} hiddenBlockStyles={hiddenBlockStyles} />
    </div>
  );
};
